import styles from './grid.scss';
import cn from 'classnames';
import { range } from 'utils';
import { createElement, ReactHTML, forwardRef } from 'react';

/*
USAGE:
<Grid desktop={5} phone={1}> // In desktop, allow 5 items on a row, on phone, allow 1.
  <Cell>
    Some content
  </Cell>
  <Cell desktop={1}> // When in desktop 
    Some content
  </Cell>
  <Cell>
    Some content
  </Cell>
</Grid>
*/

interface Props {
  children?: React.ReactNode;
  className?: string;
  gutterStyle?: string;
  gutters?: 'extraLarge' | 'large' | 'medium' | 'small' | 'hidden';
  ultra?: AvailableSizes;
  huge?: AvailableSizes;
  desktop?: AvailableSizes;
  large?: AvailableSizes;
  tablet?: AvailableSizes;
  phone?: AvailableSizes;
  /* Tag to render instead of the default div */
  tag?: keyof ReactHTML;
}

export type AvailableSizes =
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | '10'
  | '11'
  | '12';
type Breakpoints = 'ultra' | 'huge' | 'desktop' | 'large' | 'tablet' | 'phone';
type GridTypes = 'Grid' | 'Cell';

interface GridOptionsProps {
  breakpoint?: Breakpoints;
  size?: AvailableSizes;
  gridType?: GridTypes;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type OverrideStyleInterface = any;
const _styles: OverrideStyleInterface = styles;

const getClass = (className: string): string => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
  return _styles[className];
};

const getGridSizes = ({
  breakpoint,
  gridType
}: Required<Pick<GridOptionsProps, 'breakpoint' | 'gridType'>>): Record<string, string> => {
  const NUMBER_OF_OPTIONS = 12;
  const generatedRange = range(1, NUMBER_OF_OPTIONS);

  return generatedRange.reduce((sizes, size) => {
    const className = `${breakpoint}${gridType}${size}`;
    return { ...sizes, [size]: getClass(className) };
  }, {});
};

const generateOptions =
  ({ gridType }: Required<Pick<GridOptionsProps, 'gridType'>>) =>
  ({ breakpoint }: Required<Pick<GridOptionsProps, 'breakpoint'>>) =>
    getGridSizes({ gridType, breakpoint });

export const options = ({ gridType }: Required<Pick<GridOptionsProps, 'gridType'>>) => {
  const getOptionsFor = generateOptions({ gridType });

  return {
    ultra: getOptionsFor({ breakpoint: 'ultra' }),
    huge: getOptionsFor({ breakpoint: 'huge' }),
    desktop: getOptionsFor({ breakpoint: 'desktop' }),
    large: getOptionsFor({ breakpoint: 'large' }),
    tablet: getOptionsFor({ breakpoint: 'tablet' }),
    phone: getOptionsFor({ breakpoint: 'phone' })
  };
};

const getGridOptions = options({ gridType: 'Grid' });
const getCellOptions = options({ gridType: 'Cell' });

const getGridClassNames = ({ breakpoint, size }: Pick<GridOptionsProps, 'breakpoint' | 'size'>) =>
  !!breakpoint && !!size ? getGridOptions[breakpoint][size] : {};
const getCellClassNames = ({ breakpoint, size }: Pick<GridOptionsProps, 'breakpoint' | 'size'>) =>
  !!breakpoint && !!size ? getCellOptions[breakpoint][size] : {};

const gutterSizes = {
  extraLarge: styles.extraLargeGutters,
  large: styles.largeGutters,
  medium: styles.mediumGutters,
  small: styles.smallGutters,
  hidden: styles.hiddenGutters
};

export const Grid = ({
  children,
  className,
  gutters,
  ultra,
  huge,
  desktop,
  large,
  tablet,
  phone,
  gutterStyle,
  tag = 'div'
}: Props) => {
  const props = {
    className: cn(
      className,
      styles.grid,
      gutterStyle,
      gutters && gutterSizes[gutters],
      getGridClassNames({ size: ultra, breakpoint: 'ultra' }),
      getGridClassNames({ size: huge, breakpoint: 'huge' }),
      getGridClassNames({ size: desktop, breakpoint: 'desktop' }),
      getGridClassNames({ size: large, breakpoint: 'large' }),
      getGridClassNames({ size: tablet, breakpoint: 'tablet' }),
      getGridClassNames({ size: phone, breakpoint: 'phone' })
    )
  };

  return createElement(tag, props, children);
};

export const Cell = forwardRef(
  (
    {
      children,
      className,
      ultra,
      huge,
      desktop,
      large,
      tablet,
      phone,
      tag = 'div',
      ...props
    }: Pick<
      Props,
      'children' | 'className' | 'ultra' | 'huge' | 'desktop' | 'large' | 'tablet' | 'phone' | 'tag'
    > &
      React.HTMLAttributes<HTMLElement>,
    ref
  ) => {
    const _props = {
      className: cn(
        className,
        styles.cell,
        getCellClassNames({ size: ultra, breakpoint: 'ultra' }),
        getCellClassNames({ size: huge, breakpoint: 'huge' }),
        getCellClassNames({ size: desktop, breakpoint: 'desktop' }),
        getCellClassNames({ size: large, breakpoint: 'large' }),
        getCellClassNames({ size: tablet, breakpoint: 'tablet' }),
        getCellClassNames({ size: phone, breakpoint: 'phone' })
      ),
      ref,
      ...props
    };

    return createElement(tag, _props, children);
  }
);
